import type { Card, CardStatus, PushToken } from "@feed/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string
  ) {
    super(message ?? code);
  }
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

// Any 401 means the login ended (or never happened): the auth context listens and shows
// the login screen, whichever call noticed first.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "Can't reach the server");
  }
  if (response.status === 204) return undefined as T;
  const data = (await response.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  if (!response.ok) {
    // The login form's own 401 (wrong password) is an answer, not an expired session.
    if (response.status === 401 && path !== "/api/login") onUnauthorized?.();
    throw new ApiError(response.status, data?.error?.code ?? "generic", data?.error?.message);
  }
  return data as T;
}

export interface CardPage {
  cards: Card[];
  next_cursor: string | null;
}

export interface ListParams {
  status: CardStatus | "all";
  type?: string;
  before?: string;
  limit?: number;
}

function query(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined);
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : "";
}

export const api = {
  login: (password: string) => request<{ authenticated: true }>("POST", "/api/login", { password }),
  logout: () => request<void>("POST", "/api/logout"),
  session: () => request<{ authenticated: true }>("GET", "/api/session"),

  cards: {
    list: (params: ListParams) => request<CardPage>("GET", `/api/cards${query({ ...params })}`),
    setStatus: (id: string, status: CardStatus) => request<{ card: Card }>("PATCH", `/api/cards/${id}`, { status }),
    setStatusMany: (ids: string[], status: CardStatus) => request<{ updated: number }>("POST", "/api/cards/status", { ids, status }),
    purgeDismissed: () => request<{ purged: number }>("DELETE", "/api/cards?status=dismissed"),
  },
  types: () => request<{ types: { type: string; unread: number }[] }>("GET", "/api/types"),

  tokens: {
    list: () => request<{ tokens: PushToken[] }>("GET", "/api/tokens"),
    create: (label: string) => request<{ token: string; record: PushToken }>("POST", "/api/tokens", { label }),
    revoke: (id: string) => request<void>("DELETE", `/api/tokens/${id}`),
  },
};
